import React, { Key, useEffect, useMemo, useState } from "react"
import { Alert, Button, message, Table } from "antd"
import useColumns from "../hooks/useColumns"
import { ICampaignDataColumns, ITarget } from "../types/table"
import { matchTypesEnum } from "../../../types/customers"
import { matchByMatchType } from "../../../helper/filter/filters.helpers"
import Settings from "./Settings"
import { useActions } from "../../../hooks/useActions"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { TableRowSelection } from "antd/es/table/interface"
import SponsoredProductsView from "./SponsoredProductsView"
import { calcTargetMetrics } from "../services/calculateTargetMetrics"

const SponsoredProducts: React.FC = ({}) => {
  const [selectedRowCampaignIds, setSelectedRowCampaignIds] = useState<Key[]>([])
  const [expandedRowCampaignIds, setExpandedRowCampaignIds] = useState<readonly Key[]>([])
  const [selectedTargetRowCampaignIds, setSelectedTargetRowCampaignIds] = useState<Key[]>([])
  const [matchType, setMatchType] = useState<matchTypesEnum>(matchTypesEnum.ALL)
  const [campaigns, setCampaigns] = useState<ICampaignDataColumns[]>([])

  const { error, loading, campaignData } = useTypedSelector((select) => select.sponsoredProducts)
  const { campaignsColumns, targetsColumns } = useColumns(setCampaigns)
  const { fetchSponsoredProductCampaigns } = useActions()
  const { dateRange, isOrder, countryCode } = useTypedSelector((select) => select.filter)

  function loadCampaigns() {
    if (dateRange.from && dateRange.to) {
      fetchSponsoredProductCampaigns({
        startDate: dateRange.from,
        endDate: dateRange.to,
        filterIds: [],
        isOrder,
      })
    }
  }

  useEffect(() => {
    loadCampaigns()
  }, [dateRange.from, dateRange.to, isOrder, countryCode])

  useEffect(() => {
    if (error) {
      message.error(error)
    }
  }, [error])

  useEffect(() => {
    if (!campaignData.campaigns) {
      return
    }

    const campaignsLocal = campaignData.campaigns.map((campaign) => {
      const targetWithMetrics = campaign.targets?.map((target) =>
        calcTargetMetrics(campaignData, target)
      )
      return {
        ...campaign,
        targets: targetWithMetrics,
      }
    })

    setCampaigns(campaignsLocal)
  }, [campaignData])

  const data = useMemo(() => {
    return campaigns.filter((campaign) => matchByMatchType(matchType, campaign.campaignName))
  }, [campaigns, matchType])

  const rowSelection: TableRowSelection<ICampaignDataColumns> = {
    selectedRowKeys: selectedRowCampaignIds,
    onChange: setSelectedRowCampaignIds,
    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
  }

  const targetRowSelection: TableRowSelection<ITarget> = {
    selectedRowKeys: selectedTargetRowCampaignIds,
    onChange: setSelectedTargetRowCampaignIds,
  }

  function onExpandedRowsChange(expandedKeys: readonly Key[]) {
    setExpandedRowCampaignIds(expandedKeys)
  }

  return (
    <>
      <Settings
        campaignData={data}
        setExpandedRowCampaignIds={onExpandedRowsChange}
        setMatchType={setMatchType}
        matchType={matchType}
        selectedRowCampaignIds={selectedRowCampaignIds}
        setSelectedRowCampaignIds={setSelectedRowCampaignIds}
        selectedTargetRowCampaignIds={selectedTargetRowCampaignIds}
        setCampaigns={setCampaigns}
        campaigns={campaigns}
      />
      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load campaigns"
          action={
            <Button size="small" danger onClick={loadCampaigns}>
              Retry
            </Button>
          }
        />
      ) : null}
      <SponsoredProductsView
        campaignsColumns={campaignsColumns}
        data={data}
        rowSelection={rowSelection}
        loading={loading}
        targetsColumns={targetsColumns}
        targetRowSelection={targetRowSelection}
        expandedRowCampaignIds={expandedRowCampaignIds}
        onExpandedRowsChange={onExpandedRowsChange}
        lastDateWithoutData={campaignData.lastDateWithoutData}
      />
    </>
  )
}

export default SponsoredProducts
